import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, DollarSign, Star, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { pricingAPI } from '@/services/api';

// Default pricing plans
const defaultPlans = [
  {
    id: 1,
    name: 'Free',
    price: 0,
    description: 'For simple tax situations',
    features: [
      'W-2 income',
      'Standard deductions',
      'Free federal filing',
      'Email support'
    ],
    popular: false,
    active: true
  },
  {
    id: 2,
    name: 'Deluxe',
    price: 49,
    description: 'For homeowners and investors',
    features: [
      'Everything in Free',
      'Mortgage interest',
      'Investment income',
      'Priority support',
      'Audit assistance'
    ],
    popular: true,
    active: true
  },
  {
    id: 3,
    name: 'Premium',
    price: 89,
    description: 'For business owners',
    features: [
      'Everything in Deluxe',
      'Business expenses',
      'Rental property income',
      'Tax expert review',
      'Full audit protection'
    ],
    popular: false,
    active: true
  }
];

export default function PricingManagement() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    description: '',
    features: [],
    popular: false,
    active: true,
    is_business_filing: false
  });
  const [newFeature, setNewFeature] = useState('');

  // Load plans from backend
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await pricingAPI.getPlans('tax', false); // Get all tax plans
      setPlans(data);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      price: 0,
      description: '',
      features: [],
      popular: false,
      active: true,
      is_business_filing: false
    });
    setShowModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormData({ ...plan });
    setShowModal(true);
  };

  const handleDeletePlan = async (planId) => {
    try {
      await pricingAPI.deletePlan(planId);
      toast.success('Pricing plan deleted');
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete pricing plan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPlan) {
        // Update existing plan
        await pricingAPI.updatePlan(editingPlan.id, formData);
        toast.success('Pricing plan updated');
      } else {
        // Add new plan
        await pricingAPI.createPlan({ ...formData, plan_type: 'tax' });
        toast.success('Pricing plan created');
      }
      
      setShowModal(false);
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save pricing plan');
    }
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updatedFeatures });
  };

  const togglePopular = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      await pricingAPI.updatePlan(planId, { popular: !plan.popular });
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error toggling popular:', error);
      toast.error('Failed to update plan');
    }
  };

  const toggleActive = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      await pricingAPI.updatePlan(planId, { active: !plan.active });
      fetchPlans(); // Reload plans
    } catch (error) {
      console.error('Error toggling active status:', error);
      toast.error('Failed to update plan status');
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Pricing Management" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Pricing Management" user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Pricing Management</h2>
            <p className="mt-1 text-muted-foreground">
              Configure pricing plans displayed on the landing page
            </p>
          </div>
          <Button className="btn-glossy gap-2" onClick={handleAddPlan}>
            <Plus className="h-4 w-4" />
            Add Pricing Plan
          </Button>
        </div>

        {/* Pricing Plans Table */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>All Pricing Plans ({plans.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Popular</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">{plan.name}</TableCell>
                    <TableCell>
                      <Badge variant={plan.is_business_filing ? "default" : "secondary"}>
                        {plan.is_business_filing ? "Business" : "Individual"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-success" />
                        <span className="font-semibold">{plan.price}</span>
                        <span className="text-xs text-muted-foreground">/year</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{plan.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{plan.features.length} features</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={plan.active}
                          onCheckedChange={() => toggleActive(plan.id)}
                        />
                        <span className="text-sm">
                          {plan.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePopular(plan.id)}
                      >
                        <Star
                          className={`h-4 w-4 ${
                            plan.popular
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditPlan(plan)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="card-elevated border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">About Pricing Configuration</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Changes to pricing plans will be reflected immediately on the public landing page. 
                  Mark one plan as "Popular" to highlight it with a special badge. 
                  Inactive plans won't be displayed to users.
                </p>
                <p className="mt-2 text-xs text-success">
                  <strong>✓ Connected to Database:</strong> All changes are saved to the PostgreSQL database.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Plan Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="glass-strong max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit Pricing Plan' : 'Add New Pricing Plan'}
            </DialogTitle>
            <DialogDescription>
              Configure the details for this pricing tier
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Premium"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price ($/year) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., For business owners"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Features</Label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleAddFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.features.length > 0 && (
                  <div className="space-y-2 rounded-lg border p-3">
                    {formData.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded bg-muted p-2"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-success" />
                          <span className="text-sm">{feature}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFeature(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label htmlFor="popular">Mark as Popular</Label>
                <p className="text-sm text-muted-foreground">
                  This plan will be highlighted on the landing page
                </p>
              </div>
              <Switch
                id="popular"
                checked={formData.popular}
                onCheckedChange={(checked) => setFormData({ ...formData, popular: checked })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4 bg-blue-50 dark:bg-blue-900/20">
              <div>
                <Label htmlFor="is_business_filing">Business Tax Filing</Label>
                <p className="text-sm text-muted-foreground">
                  Enable this for business tax plans (shown in Business Tax section)
                </p>
              </div>
              <Switch
                id="is_business_filing"
                checked={formData.is_business_filing}
                onCheckedChange={(checked) => setFormData({ ...formData, is_business_filing: checked })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label htmlFor="active">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Only active plans are displayed to users
                </p>
              </div>
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-glossy">
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
